import type { BlastDto, MailingBlock, MailingBlockType, MailingDesign } from "../../api/mailingApi";

type Tone = "success" | "neutral" | "danger" | "info" | "warning" | "primary" | "violet";

export const BLAST_STATUS: Record<string, { label: string; tone: Tone; icon: string }> = {
  draft: { label: "טיוטה", tone: "neutral", icon: "edit" },
  scheduled: { label: "מתוזמן", tone: "info", icon: "clock" },
  sending: { label: "בשליחה", tone: "primary", icon: "play" },
  paused: { label: "מושהה", tone: "warning", icon: "pause" },
  sent: { label: "נשלח", tone: "success", icon: "check" },
  cancelled: { label: "בוטל", tone: "neutral", icon: "x" },
  active: { label: "פעיל", tone: "success", icon: "check" },
};

export const MESSAGE_STATUS: Record<string, { label: string; tone: Tone }> = {
  queued: { label: "בתור", tone: "neutral" },
  held: { label: "ממתין לתוצאת A/B", tone: "info" },
  sending: { label: "נשלח עכשיו", tone: "primary" },
  sent: { label: "נשלח", tone: "success" },
  delivered: { label: "נמסר", tone: "success" },
  bounced: { label: "חזר", tone: "danger" },
  complained: { label: "דווח כספאם", tone: "danger" },
  failed: { label: "נכשל", tone: "danger" },
  suppressed: { label: "חסום", tone: "neutral" },
  cancelled: { label: "בוטל", tone: "neutral" },
};

export const BLOCK_PALETTE: Array<{ type: MailingBlockType; label: string; icon: string }> = [
  { type: "heading", label: "כותרת", icon: "bold" },
  { type: "text", label: "טקסט", icon: "alignRight" },
  { type: "button", label: "כפתור", icon: "link" },
  { type: "image", label: "תמונה", icon: "image" },
  { type: "divider", label: "קו מפריד", icon: "minus" },
  { type: "spacer", label: "רווח", icon: "expand" },
  { type: "html", label: "HTML", icon: "code" },
];

export const blockId = () => `b${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function newBlock(type: MailingBlockType): MailingBlock {
  const id = blockId();
  switch (type) {
    case "heading":
      return { id, type, text: "כותרת", size: "l", align: "right" };
    case "text":
      return { id, type, text: "", align: "right" };
    case "button":
      return { id, type, label: "לפרטים נוספים", url: "https://", align: "center" };
    case "image":
      return { id, type, src: "", alt: "", width: 100, align: "center" };
    case "spacer":
      return { id, type, height: 24 };
    case "html":
      return { id, type, html: "" };
    default:
      return { id, type };
  }
}

export const emptyDesign = (): MailingDesign => ({ settings: {}, blocks: [newBlock("heading"), newBlock("text")] });

export const fmtNum = (n: number | null | undefined) => Number(n ?? 0).toLocaleString("he-IL");

export function pct(part: number, whole: number): string {
  if (!whole) return "—";
  return `${((part / whole) * 100).toFixed(part / whole >= 0.1 ? 0 : 1)}%`;
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString("he-IL", { day: "numeric", month: "numeric", year: "2-digit" })} ${d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}`;
}

/** the time that best describes a blast in a list */
export function blastWhen(b: BlastDto): string {
  if (b.status === "scheduled") return `מתוזמן ל־${fmtDateTime(b.scheduledAt)}`;
  if (b.status === "sent") return `נשלח ${fmtDateTime(b.finishedAt ?? b.startedAt)}`;
  if (b.status === "sending" || b.status === "paused") return `התחיל ${fmtDateTime(b.startedAt)}`;
  return `עודכן ${fmtDateTime(b.updatedAt)}`;
}

/** <input type="datetime-local"> value in local time */
export function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export const errorMessage = (e: unknown, fallback: string) => (e as { message?: string })?.message || fallback;

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
